import { CloseIcon } from "@cynthai/icons";
import { useResizeObserver } from "@reactuses/core";
import { useEffect, useRef, useState } from "react";
import { Dialog, Heading, Modal, ModalOverlay } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import Button from "../Button";

function CloseButton({ onClose }: { readonly onClose: () => void }) {
  return (
    <Button
      variant="default"
      size="square"
      aria-label="Close"
      onPress={onClose}
    >
      <CloseIcon width={24} height={24} />
    </Button>
  );
}

const drawerStyle = tv({
  base: "z-75 flex min-w-0 flex-auto items-stretch justify-end bg-background/50",
  variants: {
    size: {
      sm: { drawer: "w-[24rem]" },
      md: { drawer: "w-[28rem]" },
      lg: { drawer: "w-[31.9375rem]" },
      xl: { drawer: "w-[36rem]" },
    },
  },
  slots: {
    drawer: `
      flex h-full max-w-full flex-col border-l border-border
      bg-background-2 shadow-modal
      data-[entering]:animate-in data-[entering]:slide-in-from-right
      data-[entering]:duration-300 data-[entering]:ease-out
      data-[exiting]:animate-out data-[exiting]:slide-out-to-right
      data-[exiting]:duration-200 data-[exiting]:ease-in
    `,
    dialog: "flex h-full flex-col gap-6 p-9 outline-none",
    header: "flex w-full flex-col items-start gap-2 pb-2",
    body: "flex-1 overflow-y-auto",
    footer: "flex justify-end gap-2 border-t border-border pt-6",
    title: "text-xl font-medium text-text-primary leading-8",
    description: "text-sm font-normal text-text-tertiary",
  },
  defaultVariants: {
    size: "lg",
  },
});

export interface BaseDrawerProps extends VariantProps<typeof drawerStyle> {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onOpenChange?: (isOpen: boolean) => void;
  readonly title: string | null | React.ReactNode;
  readonly closeButton?: boolean;
  readonly children?: React.ReactNode | React.ReactNode[];
  readonly className?: string;
  readonly footer?: React.ReactNode | React.ReactNode[];
  readonly description?: React.ReactNode;
  readonly isDismissable?: boolean;
  readonly isKeyboardDismissDisabled?: boolean;
  readonly classNames?: {
    drawer?: string;
    dialog?: string;
    header?: string;
    body?: string;
    title?: string;
    footer?: string;
  };
}

const getOverlayStyle = (
  el: Element | undefined | null,
): React.CSSProperties => {
  if (!el) {
    return {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
      flexShrink: 0,
    };
  }
  const { top, left, width, height } = el.getBoundingClientRect();
  return { top, left, width, height, flexShrink: 0 };
};

export default function BaseDrawer({
  title: titleProp,
  closeButton = false,
  children,
  className,
  footer: footerProp,
  description: descriptionProp,
  isDismissable = true,
  isKeyboardDismissDisabled,
  size,
  onClose,
  onOpenChange,
  classNames,
  isOpen,
}: BaseDrawerProps) {
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});

  const sidebarEl = useRef<Element>(document.getElementById("sidebar"));

  useEffect(() => {
    setOverlayStyle(getOverlayStyle(sidebarEl.current));
  }, []);

  useResizeObserver(sidebarEl, (entries) => {
    if (entries.length !== 0) {
      setOverlayStyle(getOverlayStyle(entries[0].target));
    }
  });

  const { base, drawer, dialog, header, body, footer, title, description } =
    drawerStyle({ size });

  return (
    <ModalOverlay
      className={twMerge(
        "fixed inset-0 z-40 flex items-stretch justify-center",
      )}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      isDismissable={isDismissable}
    >
      <div style={overlayStyle}></div>
      <div className={base()}>
        <Modal className={drawer({ className: classNames?.drawer })}>
          <Dialog className={dialog({ className: classNames?.dialog })}>
            <div
              slot="title"
              className={header({ className: classNames?.header })}
            >
              <div className="flex w-full items-center justify-between">
                {titleProp && (
                  <Heading
                    slot="title"
                    className={title({ className: classNames?.title })}
                  >
                    {titleProp}
                  </Heading>
                )}
                {closeButton && <CloseButton onClose={onClose} />}
              </div>
              {descriptionProp && (
                <p className={description()}>{descriptionProp}</p>
              )}
            </div>
            {children && (
              <div
                className={body({ className: classNames?.body ?? className })}
              >
                {children}
              </div>
            )}
            {footerProp && (
              <footer className={footer({ className: classNames?.footer })}>
                {footerProp}
              </footer>
            )}
          </Dialog>
        </Modal>
      </div>
    </ModalOverlay>
  );
}
